// frontend/app/components/lessonPlans/present/usePresentTransition.js
//
// A short fade as each step arrives. It is doing one job: telling the room that
// the slide changed, so nobody spends the next ten seconds wondering whether
// they missed something. That is why it is a fade-in of the new step rather
// than a cross-fade — there is no moment where two steps are both half-visible
// and neither is readable.
//
// It is also the one piece of motion on this surface, which makes honouring
// prefers-reduced-motion cheap: with the setting on, the step simply appears.

import { useEffect, useRef } from "react";
import { Animated, Easing } from "react-native";

export const PRESENT_FADE_MS = 180;

/**
 * Read the OS/browser setting. Guarded on every side because this runs under
 * jsdom in tests (no matchMedia at all), in Electron, and on native.
 */
export const prefersReducedMotion = () => {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches === true;
  } catch (error) {
    // Some embedded webviews throw on an unsupported media query rather than
    // returning a non-match. Treat that as "no preference expressed".
    return false;
  }
};

/**
 * Returns an opacity value to hang on the step body. Re-runs whenever
 * `stepIndex` changes, which is exactly when a new slide has arrived.
 */
const usePresentTransition = (stepIndex) => {
  const reduced = prefersReducedMotion();
  const opacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (reduced) {
      opacity.setValue(1);
      return undefined;
    }

    opacity.setValue(0);
    const animation = Animated.timing(opacity, {
      toValue: 1,
      duration: PRESENT_FADE_MS,
      easing: Easing.out(Easing.quad),
      // Opacity is a CSS property under react-native-web, and the native driver
      // has nothing to drive; asking for it only produces a warning.
      useNativeDriver: false,
    });
    animation.start();
    return () => animation.stop();
  }, [opacity, reduced, stepIndex]);

  return opacity;
};

export default usePresentTransition;
